export type AiToolMeta = {
  category?: string;
  slot?: string;
  slot_type?: 'ai' | 'gec0';
  summary?: string;
  description?: string;
  icon_url?: string;
  docs_url?: string;
  label?: string;
  surface_hint?: string;
  /** SmartBlock tracker type id — used for auto-enrollment and navigation */
  tracker_type_id?: string;
};

export type AiTool = {
  id: string;
  kind: string;
  owner_role: string;
  tc_cost: number;
  uses_per_pack: number;
  remaining_uses: number;
  meta: AiToolMeta;
  slot: string;
  is_active: boolean;
};

export type AiToolsCatalogResponse = {
  tools: AiTool[];
  active_slots: Record<string, string>; // slot -> tool_id
};

/** Slot configuration for role-scoped marketplace */
export type SlotConfig = {
  id: string;
  label: string;
  type: 'ai' | 'gec0' | 'universal';
  description?: string;
};

/** Role-scoped slot limits */
export type RoleSlotConfig = {
  /** Total number of slots */
  total: number;
  /**
   * Whether slots are universal (can hold AI or SmartBlock tools).
   * If false, slots are split between AI and SmartBlock types.
   */
  universalSlots: boolean;
  /** AI-specific slots (only used when universalSlots is false) */
  ai?: number;
  /** SmartBlock-specific slots (only used when universalSlots is false) */
  gec0?: number;
};

export type PortalRole = 'teacher' | 'sponsor' | 'coach' | 'prospect' | 'guardian' | 'student';

/** Slot configuration by role */
export const ROLE_SLOT_CONFIG: Record<PortalRole, RoleSlotConfig> = {
  // Teacher: separate AI and SmartBlock slots
  teacher: { total: 6, universalSlots: false, ai: 4, gec0: 2 },
  // Sponsor: universal slots (broadest tool access, configurable OS)
  sponsor: { total: 8, universalSlots: true },
  // Coach: separate AI (creative) and SmartBlock (tracker) slots
  coach: { total: 6, universalSlots: false, ai: 3, gec0: 3 },
  // Prospect: separate AI (promotion) and SmartBlock (training tracker) slots
  prospect: { total: 4, universalSlots: false, ai: 2, gec0: 2 },
  // Guardian & Student: universal slots
  guardian: { total: 2, universalSlots: true },
  student: { total: 2, universalSlots: true },
};

/** Check if a role uses universal slots */
export function hasUniversalSlots(role: PortalRole): boolean {
  return ROLE_SLOT_CONFIG[role].universalSlots;
}

/** Get slot limits for a role (for backwards compatibility) */
export function getSlotLimits(role: PortalRole): { ai: number; gec0: number; total: number } {
  const config = ROLE_SLOT_CONFIG[role];
  if (config.universalSlots) {
    // Universal slots can hold any type
    return { ai: config.total, gec0: config.total, total: config.total };
  }
  return { ai: config.ai ?? 0, gec0: config.gec0 ?? 0, total: config.total };
}

/** Generate slot configurations for a given role */
export function getSlotsForRole(role: PortalRole): SlotConfig[] {
  const config = ROLE_SLOT_CONFIG[role];
  const slots: SlotConfig[] = [];

  if (config.universalSlots) {
    // Universal slots - can hold any tool type
    for (let i = 1; i <= config.total; i++) {
      slots.push({
        id: `slot_${i}`,
        label: `Tool Slot ${i}`,
        type: 'universal',
        description: `Tool slot ${i} (AI or SmartBlock)`,
      });
    }
  } else {
    // Separate AI and SmartBlock slots
    const aiCount = config.ai ?? 0;
    const gec0Count = config.gec0 ?? 0;

    for (let i = 1; i <= aiCount; i++) {
      slots.push({
        id: `ai_slot_${i}`,
        label: `AI Tool ${i}`,
        type: 'ai',
        description: `AI assistant slot ${i}`,
      });
    }

    for (let i = 1; i <= gec0Count; i++) {
      slots.push({
        id: `gec0_slot_${i}`,
        label: `SmartBlock ${i}`,
        type: 'gec0',
        description: `SmartBlock tracker slot ${i}`,
      });
    }
  }

  return slots;
}

/** Teacher has 4 AI slots and 2 SmartBlock slots (separate) */
export const TEACHER_SLOTS: SlotConfig[] = getSlotsForRole('teacher');

/** Sponsor has 8 universal slots */
export const SPONSOR_SLOTS: SlotConfig[] = getSlotsForRole('sponsor');

/** Coach has 6 slots (3 AI + 3 SmartBlock) */
export const COACH_SLOTS: SlotConfig[] = getSlotsForRole('coach');

/** Prospect has 4 slots (2 AI + 2 SmartBlock) */
export const PROSPECT_SLOTS: SlotConfig[] = getSlotsForRole('prospect');

/** Guardian has 2 universal slots */
export const GUARDIAN_SLOTS: SlotConfig[] = getSlotsForRole('guardian');

/** Student has 2 universal slots */
export const STUDENT_SLOTS: SlotConfig[] = getSlotsForRole('student');

/** Get slots by role string */
export function getSlotsByRole(role: string): SlotConfig[] {
  switch (role) {
    case 'teacher':
      return TEACHER_SLOTS;
    case 'sponsor':
      return SPONSOR_SLOTS;
    case 'coach':
      return COACH_SLOTS;
    case 'prospect':
      return PROSPECT_SLOTS;
    case 'guardian':
      return GUARDIAN_SLOTS;
    case 'student':
      return STUDENT_SLOTS;
    default:
      return [];
  }
}

// Legacy export for backwards compatibility
export const ROLE_SLOT_LIMITS: Record<PortalRole, { ai: number; gec0: number }> = {
  teacher: { ai: 4, gec0: 2 },
  sponsor: { ai: 8, gec0: 8 },  // Universal: can use all slots for either
  coach: { ai: 3, gec0: 3 },
  prospect: { ai: 2, gec0: 2 },
  guardian: { ai: 2, gec0: 2 },
  student: { ai: 2, gec0: 2 },
};
